import { tz } from '@date-fns/tz';
import { format, parseISO } from 'date-fns';
import cx from 'classnames';
import { centsToDollar } from '@utils/currency';
import typography from '~styles/typography.scss';
import type {
  FinancesBucketGranularity,
  FinancesEarningsTimeseriesBucket,
} from './apiHelpers';
import {
  FINANCES_CHART_HEIGHT,
  formatCompactCurrency,
  getBarHeight,
  getChartScale,
} from './chartUtils';
import styles from './EarningsByPeriodChart.scss';

export interface EarningsByPeriodChartProps {
  title: string;
  granularity?: FinancesBucketGranularity;
  buckets: FinancesEarningsTimeseriesBucket[];
  previousBuckets?: FinancesEarningsTimeseriesBucket[];
}

const EMPTY_BUCKET = { revenue: 0, refunds: 0 };

const isEmptyBucket = (bucket: FinancesEarningsTimeseriesBucket): boolean =>
  bucket.revenue === 0 && bucket.refunds === 0;

// Year-to-date (and similar month ranges) fill the whole year, so months after
// the current one come back empty. Drop those trailing $0 buckets so the chart
// ends at the latest month with activity. Trimming is driven by the current
// period — when comparing, the matching previous-period bar is dropped too.
// Daily ranges keep every bucket so short windows still show their full timeline.
const getVisibleBucketCount = (
  buckets: FinancesEarningsTimeseriesBucket[],
  granularity?: FinancesBucketGranularity,
): number => {
  if (granularity !== 'month') {
    return buckets.length;
  }

  let end = buckets.length;
  while (end > 0 && isEmptyBucket(buckets[end - 1])) {
    end -= 1;
  }

  return end === 0 ? buckets.length : end;
};

const StackedBar = ({
  revenue,
  refunds,
  max,
  variant,
}: {
  revenue: number;
  refunds: number;
  max: number;
  variant: 'current' | 'previous';
}) => {
  const revenueHeight = getBarHeight(revenue, max, FINANCES_CHART_HEIGHT);
  const refundsHeight = getBarHeight(refunds, max, FINANCES_CHART_HEIGHT);

  return (
    <div className={styles.stackedBar}>
      {revenueHeight > 0 && (
        <div
          className={cx(styles.revenueBar, {
            [styles.previousRevenueBar]: variant === 'previous',
          })}
          style={{
            height: `${revenueHeight}px`,
            ...(refundsHeight > 0 && { borderRadius: 0 }),
          }}
        />
      )}
      {refundsHeight > 0 && (
        <div
          className={cx(styles.refundsBar, {
            [styles.previousRefundsBar]: variant === 'previous',
          })}
          style={{ height: `${refundsHeight}px` }}
        />
      )}
    </div>
  );
};

// Buckets are truncated to the restaurant's timezone on the backend and
// returned as UTC instants (e.g. month start = `...T00:00:00.000Z`). We format
// in UTC so the label reflects that calendar period regardless of the browser's
// timezone — otherwise a browser behind UTC shifts every label back a period.
const UTC = { in: tz('UTC') };

const MAX_VISIBLE_X_LABELS = 12;

const formatBucketLabel = (
  bucketStart: string,
  granularity?: FinancesBucketGranularity,
  withYear = true,
): string => {
  const date = parseISO(bucketStart, UTC);

  if (granularity === 'month') {
    return format(date, withYear ? 'MMM yy' : 'MMM', UTC);
  }

  return format(date, 'MMM d', UTC);
};

// For month buckets the year is only meaningful when the range crosses calendar
// years (e.g. Nov 25 → Jan 26). Within a single year (like year-to-date) every
// label would just repeat the same year, so we show plain month names.
const shouldShowYear = (
  bucketStart: string,
  index: number,
  spansMultipleYears: boolean,
  granularity?: FinancesBucketGranularity,
): boolean => {
  if (granularity !== 'month' || !spansMultipleYears) {
    return false;
  }

  return index === 0 || parseISO(bucketStart, UTC).getUTCMonth() === 0;
};

export const EarningsByPeriodChart = ({
  title,
  granularity,
  buckets: allBuckets,
  previousBuckets: allPreviousBuckets,
}: EarningsByPeriodChartProps) => {
  const isCompare = !!allPreviousBuckets;

  const visibleCount = getVisibleBucketCount(allBuckets, granularity);
  const buckets = allBuckets.slice(0, visibleCount);
  const previousBuckets = allPreviousBuckets?.slice(0, visibleCount);

  const currentMax = Math.max(
    ...buckets.map((bucket) => bucket.revenue + bucket.refunds),
    0,
  );
  const previousMax = Math.max(
    ...(previousBuckets ?? []).map((bucket) => bucket.revenue + bucket.refunds),
    0,
  );
  const scale = getChartScale(Math.max(currentMax, previousMax));

  // Tick value -> distance from the top of the plot as a percentage.
  const tickOffsetPercent = (value: number): number =>
    (1 - value / scale.max) * 100;

  // Show at most ~12 x-axis labels so many buckets (e.g. 30 days) don't crowd
  // or wrap. Every bar is still rendered — only some labels are hidden. The last
  // label is always shown; stepped labels too close to it are hidden so the two
  // don't overlap at the right edge.
  const spansMultipleYears =
    new Set(
      buckets.map((bucket) =>
        parseISO(bucket.bucketStart, UTC).getUTCFullYear(),
      ),
    ).size > 1;

  const lastIndex = buckets.length - 1;
  const labelStep = Math.ceil(buckets.length / MAX_VISIBLE_X_LABELS);
  const isLabelVisible = (index: number): boolean =>
    index === lastIndex ||
    (index % labelStep === 0 && lastIndex - index >= labelStep);

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h3 className={cx(typography.h5, styles.title)}>{title}</h3>
        <div className={styles.legend}>
          <span className={cx(typography.c3, styles.legendItem)}>
            <span className={cx(styles.legendSwatch, styles.revenue)} />
            Revenue
          </span>
          <span className={cx(typography.c3, styles.legendItem)}>
            <span className={cx(styles.legendSwatch, styles.refunds)} />
            Refunds
          </span>
          {isCompare && (
            <span className={cx(typography.c3, styles.legendItem)}>
              <span className={cx(styles.legendSwatch, styles.previous)} />
              Previous Revenue
            </span>
          )}
          {isCompare && (
            <span className={cx(typography.c3, styles.legendItem)}>
              <span
                className={cx(styles.legendSwatch, styles.previousRefunds)}
              />
              Previous Refunds
            </span>
          )}
        </div>
      </header>

      <div className={styles.chart}>
        <div className={styles.yAxis}>
          {scale.ticks.map((tick) => (
            <span
              className={styles.yTick}
              key={tick}
              style={{ top: `${tickOffsetPercent(tick)}%` }}
            >
              {formatCompactCurrency(tick)}
            </span>
          ))}
        </div>

        <div className={styles.plotColumn}>
          <div className={styles.plotArea}>
            <div className={styles.gridLines} aria-hidden>
              {scale.ticks.map((tick, index) => (
                <div
                  className={styles.gridLine}
                  key={tick}
                  style={{
                    top: `${tickOffsetPercent(tick)}%`,
                    ...(index === 0 && {
                      borderTop: '1px solid var(--hairline)',
                    }),
                  }}
                />
              ))}
            </div>
            <div className={styles.barsRow}>
              {buckets.map((bucket, index) => {
                const previous = previousBuckets?.[index] ?? EMPTY_BUCKET;
                const label = formatBucketLabel(
                  bucket.bucketStart,
                  granularity,
                  shouldShowYear(
                    bucket.bucketStart,
                    index,
                    spansMultipleYears,
                    granularity,
                  ),
                );
                const title = isCompare
                  ? `${label}: ${centsToDollar(bucket.revenue)} revenue, ${centsToDollar(bucket.refunds)} refunds (previous: ${centsToDollar(previous.revenue)} revenue, ${centsToDollar(previous.refunds)} refunds)`
                  : `${label}: ${centsToDollar(bucket.revenue)} revenue, ${centsToDollar(bucket.refunds)} refunds`;

                return (
                  <div
                    className={styles.barGroup}
                    key={bucket.bucketStart}
                    title={title}
                  >
                    <div className={styles.bars}>
                      <StackedBar
                        max={scale.max}
                        refunds={bucket.refunds}
                        revenue={bucket.revenue}
                        variant="current"
                      />
                      {isCompare && (
                        <StackedBar
                          max={scale.max}
                          refunds={previous.refunds}
                          revenue={previous.revenue}
                          variant="previous"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.xLabels}>
            {buckets.map((bucket, index) => (
              <span
                className={cx(typography.c3, styles.xLabel)}
                key={bucket.bucketStart}
              >
                {isLabelVisible(index)
                  ? formatBucketLabel(
                      bucket.bucketStart,
                      granularity,
                      shouldShowYear(
                        bucket.bucketStart,
                        index,
                        spansMultipleYears,
                        granularity,
                      ),
                    )
                  : ''}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
